import React from "react";
import {
  Flex,
  Image,
  Text,
  Spinner,
  Button,
  Icon,
  Box,
  Heading,
  Link,
} from "@chakra-ui/core";
import { TextField } from "@material-ui/core";
import { withStyles } from "@material-ui/core/styles";
import { NewsletterSignupForm } from "components/forms/NewsletterSignupForm";
import { FormErrorMessageListener } from "components/listeners/FormErrorMessageListener";
import { baseUrl } from "services/shared/Environment";
import { getCategorySlug, getCategoryName } from "data/api/ApiCategory";
import theme from "libraries/styled-components/theme";
import { useCategories, useTags } from "./optimized/Footer/Footer.hooks";
import ApiTag from "models/data/api/ApiTag";

export function FooterContainer({ children }) {
  return (
    <Flex
      alignItems="center"
      justifyContent="center"
      w="100%"
      bg="#1c1c1a"
      p={10}
      zIndex={1}
    >
      <Flex w="100%" maxW="1000px" flexDirection="column">
        {children}
      </Flex>
    </Flex>
  );
}

export function LogoAndDescription() {
  return (
    <Flex flexDirection="column">
      <Image
        htmlHeight="50"
        htmlWidth="50"
        src="/static/lettermark_logo.png"
        alt="logo"
      />
      <Text mt={4} color="gray.300" maxW="180px" fontFamily="Poppins">
        The Juggernaut tells untold, smart South Asian stories and news you
        won't find anywhere else.
      </Text>
    </Flex>
  );
}

export function NewsletterSignup({ emailFieldProps, signedUp }) {
  return (
    <Box w="100%" maxW="300px">
      <Heading fontSize="sm" color="white">
        The Weekly
      </Heading>
      <Text fontFamily="Poppins" color="gray.300" mt={2} mb={4}>
        It’s like your other email briefings. But Browner. Join thousands and
        get the best newsletter that curates the global news on South Asia(ns)
        every Sunday.
      </Text>
      {!signedUp ? (
        <EmailField {...emailFieldProps} />
      ) : (
        <Text
          fontWeight="bold"
          fontFamily="Poppins"
          color={theme.colors.orange}
        >
          Thanks for signing up!
        </Text>
      )}
    </Box>
  );
}

export function FooterSectionHeading({ children }) {
  return (
    <Heading
      fontSize="sm"
      color="white"
      textTransform="uppercase"
      fontWeight="medium"
    >
      {children}
    </Heading>
  );
}

export function FooterLink({ children, href }) {
  return (
    <Link
      fontFamily="Poppins"
      color="gray.400"
      mt={3}
      textTransform="uppercase"
      fontWeight="medium"
      target="_blank"
      rel="noopener"
      href={href}
    >
      {children}
    </Link>
  );
}

export function MobileSectionDivider() {
  return (
    <Box
      w="100%"
      mt={10}
      h={"1px"}
      display={["block", "block", "none"]}
      bg="gray.400"
    />
  );
}

export function FooterLinks({ children }) {
  return (
    <Flex
      flexGrow={1}
      mt={[6, 6, 0]}
      bg="#1c1c1a"
      justifyContent="flex-end"
      flexDirection={["column", "column", "row"]}
    >
      {children}
    </Flex>
  );
}

export function FooterSection({ children }) {
  return (
    <Flex bg="#1c1c1a" mx={[0, 0, 6]} my={[6, 6, 0]} flexDirection="column">
      {children}
    </Flex>
  );
}

export function FooterSectionDivider() {
  return <Box w="100%" bg="gray.400" h="1px" my={10} />;
}

export function NewsletterAndLinksSection({ children }) {
  return <Flex flexDirection={["column", "column", "row"]}>{children}</Flex>;
}

export function FooterLinksSection({ categories, tags }) {
  return (
    <FooterLinks>
      <FooterSection>
        <FooterSectionHeading>Categories</FooterSectionHeading>
        {categories.map((category) => (
          <FooterLink
            href={`${baseUrl()}/category/${getCategorySlug(category)}`}
            key={category.id}
          >
            {getCategoryName(category)}
          </FooterLink>
        ))}
      </FooterSection>
      <FooterSection>
        <FooterSectionHeading>Tags</FooterSectionHeading>
        {tags.map((tag) => (
          <FooterLink href={`${baseUrl()}/tag/${ApiTag.id(tag)}`} key={tag.id}>
            {ApiTag.tagName(tag)}
          </FooterLink>
        ))}
      </FooterSection>
      <FooterSection>
        <FooterSectionHeading>HQ</FooterSectionHeading>
        <FooterLink href={`https://www.thejuggernaut.com/about`}>
          About
        </FooterLink>
        <FooterLink href={`https://www.thejuggernaut.com/ethics`}>
          Ethics Policy
        </FooterLink>
        <FooterLink href={`https://www.thejuggernaut.com/newsletter`}>
          Newsletter
        </FooterLink>
        <FooterLink href={`https://forms.gle/vCUB4HbKA8AyfL8j9`}>
          Pitch Us
        </FooterLink>
        <FooterLink href={`https://the-juggernaut-store.myshopify.com/`}>
          Shop
        </FooterLink>
        <FooterLink href={`https://jobs.lever.co/thejuggernaut`}>
          Careers
        </FooterLink>
        <div style={{ height: 20 }} />
        <FooterSectionHeading>Support</FooterSectionHeading>
        <FooterLink href={`https://juggernaut.zendesk.com/hc`}>FAQs</FooterLink>
        <FooterLink href={`https://www.thejuggernaut.com/contact`}>
          Contact Us
        </FooterLink>
      </FooterSection>
      <FooterSection>
        <FooterSectionHeading>Follow</FooterSectionHeading>
        <FooterLink href={`https://www.instagram.com/_thejuggernaut/`}>
          Instagram
        </FooterLink>
        <FooterLink href={`https://www.facebook.com/bethejuggernaut`}>
          Facebook
        </FooterLink>
        <FooterLink href={`https://twitter.com/bethejuggernaut`}>
          Twitter
        </FooterLink>
        <FooterLink href={`https://www.linkedin.com/company/thejuggernaut`}>
          LinkedIn
        </FooterLink>
        <FooterLink
          href={`https://www.youtube.com/channel/UCP6qX7Ukp7M84bFJMHDuO-Q`}
        >
          YouTube
        </FooterLink>
      </FooterSection>
    </FooterLinks>
  );
}

const CustomTextField = withStyles({
  root: {
    "& label.Mui-focused": {
      color: "white",
    },
    "& .MuiInput-underline:after": {
      borderBottomColor: "white",
    },
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        borderColor: "white",
      },
      "&:hover fieldset": {
        borderColor: "white",
      },
      "&.Mui-focused fieldset": {
        borderColor: "white",
      },
    },
  },
})(TextField);

export function EmailField({
  value,
  hasError,
  onChange,
  disabled,
  onClick,
  isSubmitting,
}) {
  return (
    <Flex flexDirection={["row", "row", "row"]}>
      <CustomTextField
        variant="outlined"
        size="small"
        id="standard-adornment-password"
        type={"text"}
        placeholder="Email"
        value={value}
        error={hasError}
        disabled={disabled}
        style={{
          fontFamily: "Poppins",
          backgroundColor: "white",
          color: "white",
          borderRadius: `4px 0 0 4px`,
        }}
        onChange={onChange}
      ></CustomTextField>
      <Button
        _hover={{ backgroundColor: theme.colors.orange }}
        _active={{ backgroundColor: theme.colors.orange }}
        roundedLeft={0}
        onClick={onClick}
        bg={theme.colors.orange}
        aria-label="Submit"
      >
        {isSubmitting ? (
          <Spinner size="sm" />
        ) : (
          <Icon color="white" name="arrow-forward" />
        )}
      </Button>
    </Flex>
  );
}

export function PolicyLinks() {
  return (
    <Flex flexDirection={["column", "column", "row"]}>
      <Link
        fontFamily="Poppins"
        color="gray.400"
        textTransform="uppercase"
        href={`https://www.thejuggernaut.com/copyright`}
        target="_blank"
        rel="noopener"
      >
        © 2021 THE JUGGERNAUT® ALL RIGHTS RESERVED.
      </Link>
      <Flex
        flexGrow={1}
        justifyContent="flex-end"
        flexDirection={["column", "column", "row"]}
        mt={[4, 4, 0]}
      >
        <Link
          fontFamily="Poppins"
          color="gray.400"
          textTransform="uppercase"
          mr={[0, 0, 8]}
          href={`https://www.thejuggernaut.com/privacy`}
          target="_blank"
          rel="noopener"
        >
          Privacy Policy
        </Link>
        <Link
          fontFamily="Poppins"
          color="gray.400"
          textTransform="uppercase"
          mt={[4, 4, 0]}
          href={`https://www.thejuggernaut.com/terms`}
          target="_blank"
          rel="noopener"
        >
          Terms of Use
        </Link>
      </Flex>
    </Flex>
  );
}

export function Footer({
  emailFieldProps,
  signedUpForNewsletter,
  categories,
  tags,
}) {
  return (
    <FooterContainer>
      <LogoAndDescription />
      <FooterSectionDivider />
      <NewsletterAndLinksSection>
        <NewsletterSignup
          signedUp={signedUpForNewsletter}
          emailFieldProps={emailFieldProps}
        />
        <MobileSectionDivider />
        <FooterLinksSection categories={categories} tags={tags} />
      </NewsletterAndLinksSection>
      <FooterSectionDivider />
      <PolicyLinks />
    </FooterContainer>
  );
}

export function FooterWithState() {
  const { categories } = useCategories();
  const { tags } = useTags();

  return (
    <NewsletterSignupForm>
      {({
        values,
        setFieldValue,
        handleSubmit,
        isSubmitting,
        errors,
        submitCount,
        someFieldHasError,
        getErrorMessage,
        signedUpSuccessfully,
      }) => (
        <>
          <FormErrorMessageListener
            errors={errors}
            submitCount={submitCount}
            someFieldHasError={someFieldHasError}
            getErrorMessage={getErrorMessage}
          />
          <Footer
            categories={categories}
            tags={tags}
            signedUpForNewsletter={signedUpSuccessfully}
            emailFieldProps={{
              value: values?.email,
              hasError: submitCount > 0 && Boolean(errors?.email),
              onChange: (e) => setFieldValue("email", e.target.value),
              disabled: isSubmitting,
              onClick: handleSubmit,
              isSubmitting,
            }}
          />
        </>
      )}
    </NewsletterSignupForm>
  );
}
